import { createAskUserQuestionView, extractAskUserQuestions } from './ask-user-question';
import { renderMarkdown } from './markdown';
import { stringifyToolValue, toolKind } from './tool-model';
import type { ToolCallSegment } from './types';

function buildStructuredToolSection(labelText: string, bodyText: string): HTMLDivElement {
  const section = document.createElement('div');
  section.className = 'tool-call-section';
  const label = document.createElement('div');
  label.className = 'tool-call-section-label';
  label.textContent = labelText;
  const pre = document.createElement('pre');
  pre.className = 'tool-call-code';
  pre.textContent = bodyText;
  section.appendChild(label);
  section.appendChild(pre);
  return section;
}

function commandTextFromInput(input: unknown, fallback: unknown): string {
  const command = (input as { command?: unknown } | null | undefined)?.command;
  if (typeof command === 'string') return command;
  return typeof fallback === 'string' ? fallback : '';
}

function renderCommandToolContent(tool: ToolCallSegment): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'tool-call-content command';
  const stack = document.createElement('div');
  stack.className = 'tool-call-structured';
  const commandText = commandTextFromInput(tool.input, tool.meta?.subtitle);
  if (commandText) stack.appendChild(buildStructuredToolSection('Command', commandText));
  if (tool.result) {
    stack.appendChild(buildStructuredToolSection('Output', stringifyToolValue(tool.result)));
  } else if (!tool.done) {
    const empty = document.createElement('div');
    empty.className = 'tool-call-empty';
    empty.textContent = '等待命令输出…';
    stack.appendChild(empty);
  }
  wrapper.appendChild(stack);
  return wrapper;
}

function renderReasoningToolContent(tool: ToolCallSegment): HTMLElement {
  const content = document.createElement('div');
  content.className = 'tool-call-content reasoning';
  const text = stringifyToolValue(tool.result || tool.input);
  content.innerHTML = text ? renderMarkdown(text) : '<div class="tool-call-empty">暂无推理内容</div>';
  return content;
}

function renderStructuredPayloadToolContent(tool: ToolCallSegment): HTMLElement {
  const kind = toolKind(tool);
  const wrapper = document.createElement('div');
  wrapper.className = `tool-call-content ${kind === 'file_change' ? 'file-change' : ''}`.trim();
  const stack = document.createElement('div');
  stack.className = 'tool-call-structured';
  if (typeof tool?.meta?.subtitle === 'string') {
    stack.appendChild(buildStructuredToolSection(kind === 'file_change' ? 'Target' : 'Tool', tool.meta.subtitle));
  }
  const payloadText = stringifyToolValue(tool.result || tool.input);
  if (payloadText) {
    stack.appendChild(buildStructuredToolSection('Payload', payloadText));
  }
  wrapper.appendChild(stack);
  return wrapper;
}

function renderRuntimeErrorToolContent(tool: ToolCallSegment): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'tool-call-content';
  const stack = document.createElement('div');
  stack.className = 'tool-call-structured';
  const input = tool.input as { message?: unknown; inputAvailability?: unknown; activeToolCallsSnapshot?: unknown } | null | undefined;
  if (typeof input?.message === 'string') stack.appendChild(buildStructuredToolSection('Message', input.message));
  if (typeof input?.inputAvailability === 'string') stack.appendChild(buildStructuredToolSection('Input Availability', input.inputAvailability));
  if (input?.activeToolCallsSnapshot) stack.appendChild(buildStructuredToolSection('Active Tool Calls', stringifyToolValue(input.activeToolCallsSnapshot)));
  if (tool.result) stack.appendChild(buildStructuredToolSection('Audit Result', stringifyToolValue(tool.result)));
  wrapper.appendChild(stack);
  return wrapper;
}

function renderDefaultToolContent(tool: ToolCallSegment): HTMLElement {
  const inputStr = stringifyToolValue(tool.result || tool.input);
  const content = document.createElement('div');
  content.className = 'tool-call-content';
  content.textContent = inputStr;
  return content;
}

export function buildToolContentElement(tool: ToolCallSegment): HTMLElement {
  if (tool.name === 'AskUserQuestion') {
    const questions = extractAskUserQuestions(tool.input);
    if (questions.length > 0) {
      return createAskUserQuestionView(questions);
    }
  }

  const kind = toolKind(tool);
  if (kind === 'command_execution') return renderCommandToolContent(tool);
  if (kind === 'reasoning') return renderReasoningToolContent(tool);
  if (kind === 'runtime_error') return renderRuntimeErrorToolContent(tool);
  if (kind === 'file_change' || kind === 'mcp_tool_call') return renderStructuredPayloadToolContent(tool);
  return renderDefaultToolContent(tool);
}
